import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/MagnifyingGlass";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { Menu } from "@freightsimple/ui-core/Menu/Menu";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import type { CSSProperties } from "react";
import { useAppSiteMargin } from "@/Shared/Hooks/useAppSiteMargin";
import { useWidth } from "@/Shared/Hooks/useWidth";
import { NavBarPairMobileDeviceButton } from "../Buttons/NavBarPairMobileDeviceButton";
import { NavBarSignOutButton } from "../Buttons/NavBarSignOutButton";
import { NavBarSwitchOrganizationButton } from "../Buttons/NavBarSwitchOrganizationButton";
import { NavBarThemeMenu } from "../Menus/NavBarThemeMenu";
import { NavBarOverflowNav } from "./Internal/navBarTopBarOverflow";
import type { TopNavBarProps } from "./Internal/navBarTopBarPrimitives";

/*
 * RENOVATION REGISTER — NavBarTopBar
 * A ui-core-composed replacement for the two-row Cargo NavBar (top bar +
 * SubNavBar). Presentational: a container wires auth / coupon / invoice hooks
 * and passes data + handlers in. Dashboard-specific chrome (coupon pill,
 * invoice warning, currency tag, employee dev menu) comes in as slots so this
 * stays a pure ui-core composition.
 *
 * Open:
 * - Ghost search is hidden (search isn't supported yet); style it for the
 *   light bar when we turn it on.
 */

function UserMenu_({
  userName,
  companyName,
  currencyTag,
  onNavigate,
  showPairMobile,
  devMenu,
}: Pick<
  TopNavBarProps,
  | "userName"
  | "companyName"
  | "currencyTag"
  | "onNavigate"
  | "showPairMobile"
  | "devMenu"
>) {
  return (
    // Default width, not `wide`: the longest row here is "Switch Organization",
    // which the default 208px holds comfortably. At `wide` the panel ran 48px
    // past its content and left the Theme submenu's caret stranded well short
    // of the corner.
    <Menu
      trigger={
        <button
          type="button"
          data-testid="user-dropdown"
          className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-muted"
        >
          <div className="flex max-w-[200px] min-w-0 flex-col items-start leading-tight">
            <span className="max-w-full truncate text-xs font-semibold text-foreground">
              {userName}
            </span>
            <span className="hidden max-w-full items-center gap-1.5 text-[11px] text-content-muted lg:flex">
              <span className="min-w-0 truncate">{companyName}</span>
              {currencyTag}
            </span>
          </div>
          <CaretDownIcon
            className="h-3.5 w-3.5 text-content-muted"
            weight="bold"
          />
        </button>
      }
    >
      <NavBarSwitchOrganizationButton />
      {showPairMobile && (
        <NavBarPairMobileDeviceButton onNavigate={onNavigate} />
      )}
      <NavBarThemeMenu />
      {devMenu}
      <NavBarSignOutButton />
    </Menu>
  );
}

/**
 * Customer navigation bar, composed from ui-core. Replaces the legacy Cargo
 * NavBar with a single row: brand + primary nav (which overflows into a "More"
 * menu as space shrinks) on the left, and the quotes CTA + user menu on the
 * right. Cypress hooks (`subnav-*`, `user-dropdown`,
 * `switch-organization-menu-option`) are preserved.
 */
export function NavBarTopBar({
  activePath,
  onNavigate,
  onHelpCenter,
  showDrafts,
  showDevelopers,
  showPairMobile,
  showGetQuotes,
  onGetQuotes,
  onReferralCode,
  userName,
  companyName,
  currencyTag,
  coupon,
  invoiceWarning,
  demoLabel,
  devMenu,
  showSearch = false,
}: TopNavBarProps) {
  // Below `md` the bar can't hold the quotes CTA as well as the logo and user
  // menu — the CTA's label can't wrap, so it pushed the user menu off-screen
  // and gave every signed-in page a horizontal scroll. On a phone the CTA
  // moves into the ☰ menu instead, mirroring how NavBarSignedOut collapses.
  const [rowRef, rowWidth] = useWidth();
  // A measured number, not a designed one — passed through as CSSProperties the
  // way AppSiteMargin does, which is what the no-inline-styles check allows.
  const siteMargin = useAppSiteMargin();
  const gutter: CSSProperties = {
    paddingLeft: siteMargin,
    paddingRight: siteMargin,
  };
  const isNarrow = rowWidth > 0 && rowWidth < 768;

  return (
    // The bar spans full width, and so does its content row — matching the page
    // content (which is full-width with a small side margin). A max-width cap
    // here starved the primary nav on wide screens and forced items (e.g.
    // Financial) into the overflow menu even with abundant empty space.
    <nav className="border-b border-border bg-card">
      {/* The gutter is the app's measured site margin, not a spacing token, so
          the logo lines up with the page heading below it and the right edges
          agree. `useAppSiteMargin` is the single source both this and the routed
          content read. */}
      <div
        ref={rowRef}
        className="flex h-14 w-full items-center gap-2 sm:gap-3"
        style={gutter}
      >
        {/* Left — brand. The logo is drawn at 0.75 via transform, which leaves
          empty layout box on its right; -mr-11 reclaims that phantom gap so the
          nav sits close to the logo. */}
        <div className="flex shrink-0 items-center gap-2">
          <div className="-mr-11 origin-left scale-[0.75]">
            <Logo />
          </div>
          {demoLabel}
        </div>

        {/* Middle — primary nav, overflowing into a "More" menu as space shrinks */}
        <NavBarOverflowNav
          activePath={activePath}
          onNavigate={onNavigate}
          onHelpCenter={onHelpCenter}
          onReferralCode={onReferralCode}
          showDrafts={showDrafts}
          showDevelopers={showDevelopers}
          quotesCtaInMenu={isNarrow && showGetQuotes}
        />

        {/* Right — search, coupon, invoice, quotes CTA, user menu. Allowed to
          shrink (the user block truncates) so the nav never forces the page
          wider than a phone viewport — a rigid nav gave the body a horizontal
          scroll and a white strip beside the full-bleed map. */}
        <div className="flex min-w-0 shrink items-center gap-2">
          {showSearch && (
            <div className="w-56">
              <TextInput
                value=""
                onChange={() => {}}
                placeholder="Search"
                variant="ghost"
                startIcon={MagnifyingGlassIcon}
                width="full"
              />
            </div>
          )}
          {coupon}
          {invoiceWarning}
          {/* Slides the quotes CTA open/closed as the booking flow starts/ends
            (max-width + opacity so it works cross-browser and never clips). */}
          {!isNarrow && (
            <div
              className={`shrink-0 overflow-hidden rounded-md transition-all duration-300 ease-out ${
                showGetQuotes
                  ? "max-w-[240px] opacity-100"
                  : "max-w-0 opacity-0"
              }`}
            >
              <div className="w-max">
                <Button primary size="compact" onClick={onGetQuotes}>
                  Get Instant Quotes
                </Button>
              </div>
            </div>
          )}
          <div className="hidden h-7 w-px bg-border sm:block" />
          <UserMenu_
            userName={userName}
            companyName={companyName}
            currencyTag={currencyTag}
            onNavigate={onNavigate}
            showPairMobile={showPairMobile}
            devMenu={devMenu}
          />
        </div>
      </div>
    </nav>
  );
}
